/**
 * Route guard, used as a layout route.
 *
 *     { element: <ProtectedRoute />, children: [...] }
 *     { element: <ProtectedRoute view="settings" />, children: [...] }
 *     { element: <ProtectedRoute staffOnly />, children: [...] }
 *
 * Being signed out redirects to the login screen. Being signed in but not
 * allowed renders a 403 page instead: bouncing the user somewhere else hides
 * *why* they cannot see the screen, and if the destination is also protected
 * it loops.
 *
 * `staffOnly` is the only gate kdoshsop has today. When the backend grows a
 * role model this component gains `permission` and `role` props and consults
 * `usePermission`; the `view` prop already routes through the same registry,
 * so a route declared with `view` needs no change at all.
 */
import { Navigate, Outlet, useLocation } from 'react-router';

import type { ViewKey } from '@/config/views';
import { Forbidden } from '@/shared/components/Forbidden';
import { FullPageLoader } from '@/shared/components/FullPageLoader';
import { useCanSee, useIsStaff } from '@/shared/hooks/usePermission';

import { useAuth } from '../hooks/useAuth';

interface Props {
  view?: ViewKey;
  staffOnly?: boolean | undefined;
}

export function ProtectedRoute({ view, staffOnly }: Props) {
  const { isAuthenticated, isRestoring } = useAuth();
  const location = useLocation();
  const canSee = useCanSee(view);
  const isStaff = useIsStaff();

  // Must wait: rendering the redirect before the session is restored would
  // bounce every authenticated user to the login screen on a page refresh.
  if (isRestoring) return <FullPageLoader />;

  if (!isAuthenticated) {
    // `state.from` lets the login screen return the user where they meant to go.
    return <Navigate to="/ingresar" replace state={{ from: location }} />;
  }

  if (view && !canSee) return <Forbidden />;
  if (staffOnly && !isStaff) return <Forbidden />;

  return <Outlet />;
}
